import { useState, useEffect, useCallback, RefObject } from 'react';
import { useHapticFeedback } from './useHapticFeedback';

interface PullToRefreshOptions {
  onRefresh: () => Promise<void>;
  threshold?: number;
  maxPull?: number;
  containerRef?: RefObject<HTMLElement>;
}

export const usePullToRefresh = ({
  onRefresh,
  threshold = 80,
  maxPull = 150,
  containerRef
}: PullToRefreshOptions) => {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [startY, setStartY] = useState(0);
  const { vibrate } = useHapticFeedback();

  const handleTouchStart = useCallback((e: TouchEvent) => {
    // Use requestAnimationFrame to batch layout reads and avoid forced reflow
    requestAnimationFrame(() => {
      const container = containerRef?.current || document.documentElement;
      if (container.scrollTop === 0 && !isRefreshing) {
        setStartY(e.touches[0].clientY);
      }
    });
  }, [isRefreshing, containerRef]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (startY === 0 || isRefreshing) return;

    const currentY = e.touches[0].clientY;
    const distance = currentY - startY;

    if (distance > 0) {
      const pull = Math.min(distance * 0.5, maxPull);
      setPullDistance(pull);

      if (pull >= threshold) {
        vibrate('light');
      }
    }
  }, [startY, isRefreshing, threshold, maxPull, vibrate]);

  const handleTouchEnd = useCallback(async () => {
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      vibrate('success');
      
      try {
        await onRefresh();
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
    setStartY(0);
  }, [pullDistance, threshold, isRefreshing, onRefresh, vibrate]);

  useEffect(() => {
    const target = containerRef?.current || window;
    
    target.addEventListener('touchstart', handleTouchStart as any);
    target.addEventListener('touchmove', handleTouchMove as any);
    target.addEventListener('touchend', handleTouchEnd as any);

    return () => {
      target.removeEventListener('touchstart', handleTouchStart as any);
      target.removeEventListener('touchmove', handleTouchMove as any);
      target.removeEventListener('touchend', handleTouchEnd as any);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd, containerRef]);

  const progress = Math.min(pullDistance / threshold, 1);

  return {
    pullDistance,
    isRefreshing,
    progress,
    isPulling: pullDistance > 0
  };
};
